import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import MerchantLoginPage from './pages/MerchantLoginPage';
import DashboardPage from './pages/DashboardPage';
import PaymentsPage from './pages/PaymentsPage';
import UsersPage from './pages/UsersPage';
import MerchantsPage from './pages/MerchantsPage';
import ReportsPage from './pages/ReportsPage';
import AMLPage from './pages/AMLPage';
import ProvidersPage from './pages/ProvidersPage';
import TerminalsPage from './pages/TerminalsPage';
import CommissionsPage from './pages/CommissionsPage';
import AuditPage from './pages/AuditPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import WebhooksPage from './pages/WebhooksPage';
import PayoutsPage from './pages/PayoutsPage';
import MerchantCabinetPage from './pages/MerchantCabinetPage';
import './App.css';

// Check if user is a merchant (only has impersonate_token, no admin user)
const isMerchantOnly = () => {
  const hasImpersonateToken = !!localStorage.getItem('impersonate_token');
  const hasAdminToken = !!localStorage.getItem('token');
  const hasAdminUser = !!localStorage.getItem('user');
  // Merchant only if has impersonate token but NO admin session
  return hasImpersonateToken && (!hasAdminToken || !hasAdminUser);
};

// Route for admin pages - blocks merchants
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // If merchant is trying to access admin routes, redirect to cabinet
  if (isMerchantOnly()) {
    return <Navigate to="/cabinet" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Route for merchant cabinet - allows access with impersonate token
const CabinetRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const hasImpersonateToken = !!localStorage.getItem('impersonate_token');

  if (loading && !hasImpersonateToken) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Allow access if user is logged in OR has impersonate token
  if (!user && !hasImpersonateToken) {
    return <Navigate to="/merchant-login" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // If merchant, redirect to cabinet
  if (isMerchantOnly()) {
    return <Navigate to="/cabinet" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Merchant login route - if already logged in as merchant, go to cabinet
const MerchantLoginRoute = ({ children }) => {
  const hasImpersonateToken = !!localStorage.getItem('impersonate_token');
  
  if (hasImpersonateToken) {
    return <Navigate to="/cabinet" replace />;
  }
  
  return children;
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/merchant-login" element={<MerchantLoginRoute><MerchantLoginPage /></MerchantLoginRoute>} />
            
            {/* Admin routes - blocked for merchants */}
            <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/payments" element={<ProtectedRoute><PaymentsPage /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
            <Route path="/merchants" element={<ProtectedRoute><MerchantsPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/aml" element={<ProtectedRoute><AMLPage /></ProtectedRoute>} />
            <Route path="/providers" element={<ProtectedRoute><ProvidersPage /></ProtectedRoute>} />
            <Route path="/terminals" element={<ProtectedRoute><TerminalsPage /></ProtectedRoute>} />
            <Route path="/commissions" element={<ProtectedRoute><CommissionsPage /></ProtectedRoute>} />
            <Route path="/audit" element={<ProtectedRoute><AuditPage /></ProtectedRoute>} />
            <Route path="/notification-settings" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/webhooks" element={<ProtectedRoute><WebhooksPage /></ProtectedRoute>} />
            <Route path="/payouts" element={<ProtectedRoute><PayoutsPage /></ProtectedRoute>} />
            
            {/* Merchant cabinet - allowed for both admin and merchants */}
            <Route path="/cabinet" element={<CabinetRoute><MerchantCabinetPage /></CabinetRoute>} />
            
            {/* Catch all - redirect based on user type */}
            <Route path="*" element={
              isMerchantOnly() 
                ? <Navigate to="/cabinet" replace />
                : <Navigate to="/" replace />
            } />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
